/**
 * Copyright (c) 2025-2026 eBUCIS, LLC. All rights reserved.
 *
 * Layout Component - Main application layout with sidebar and header
 *
 * This software is proprietary and confidential. Unauthorized copying,
 * distribution, or modification is strictly prohibited.
 */

import { useEffect, useState, useCallback } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import { useQueryClient } from 'react-query'
import { useAuthStore } from '../stores/authStore'
import webSocketService from '../services/webSocketService'
import Sidebar from './Sidebar'
import Header from './Header'
import PullToRefreshContainer from './PullToRefreshContainer'
import PatentPendingBadge from './PatentPendingBadge'
import AppCopyright from './AppCopyright'
import { BadgeNotification } from './badges'
import { createLogger } from '../utils/logger'

const logger = createLogger('Layout')

const Layout = () => {
  const user = useAuthStore((state) => state.user)
  const token = useAuthStore((state) => state.token)
  const isImpersonating = useAuthStore((state) => state.isImpersonating)
  const location = useLocation()
  const queryClient = useQueryClient()

  // Key for forcing Outlet remount on pull-to-refresh
  const [refreshKey, setRefreshKey] = useState(0)

  // Check if we're on a challenge session or calculator route - use full-width
  const isChallengeRoute = location.pathname.startsWith('/challenge-session/')
  const isCalculatorRoute = location.pathname === '/calculator'
  const isFullWidthRoute = isChallengeRoute || isCalculatorRoute

  // Pull-to-refresh handler
  const handleRefresh = useCallback(async () => {
    // Invalidate all React Query cache
    await queryClient.invalidateQueries()

    // Increment key to force Outlet remount (triggers useEffect data fetching)
    setRefreshKey((prev) => prev + 1)

    // Small delay for visual feedback
    await new Promise((resolve) => setTimeout(resolve, 300))
  }, [queryClient])

  // Connect WebSocket when user is authenticated
  useEffect(() => {
    if (user) {
      logger.debug('🔌 Layout: Connecting WebSocket for authenticated user')
      webSocketService.connect(token)
    }
    // Don't disconnect on unmount - keep connection alive across navigation
    // Disconnect is handled by logout in authStore
  }, [user, token])

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col">
      <div className="flex flex-1">
        {/* Sidebar */}
        <Sidebar />

        {/* Main content - Add left margin for fixed sidebar on desktop */}
        <div className="flex-1 flex flex-col lg:ml-64 min-w-0 overflow-x-hidden">
          {/* Header */}
          <Header />

          {/* Page content - Add top margin for fixed header (h-16 = 64px) + impersonation banner (h-10 = 40px) */}
          {/* Full-width routes (challenge, calculator): no padding, constrained height for seamless experience */}
          <main
            className={`flex-1 ${
              isFullWidthRoute ? 'p-0 overflow-hidden' : 'p-6 pb-16'
            } ${isImpersonating ? 'mt-[104px]' : 'mt-16'}`}
            style={
              isFullWidthRoute
                ? { height: `calc(100dvh - ${isImpersonating ? '104px' : '64px'})` }
                : undefined
            }
          >
            <PullToRefreshContainer
              onRefresh={handleRefresh}
              enabled={!isFullWidthRoute}
              className={isFullWidthRoute ? 'h-full' : ''}
            >
              <Outlet key={refreshKey} />
            </PullToRefreshContainer>
          </main>

          {/* App footer with pending-patent notice (skipped on full-width
              challenge/calculator routes to preserve their seamless height) */}
          {!isFullWidthRoute && (
            <footer className="fixed bottom-0 left-0 lg:left-64 right-0 z-30 flex items-center justify-center gap-3 border-t border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900 px-6 py-3 text-xs text-gray-400 dark:text-gray-500">
              <PatentPendingBadge />
              <AppCopyright />
            </footer>
          )}
        </div>
      </div>

      {/* Badge Achievement Notifications */}
      {user && <BadgeNotification />}
    </div>
  )
}

export default Layout
